import React from "react";
import { CheckCircle2 } from "lucide-react";
import { SectionHeading, PillLink } from "../components/common";
import { GuideMascot } from "../components/GuideMascot";
import { Leaf, Star } from "../components/Decor";
import { PHOTOS, GUIDES, PHILOSOPHY_PRINCIPLES } from "../data/content";

const PageHero = ({ eyebrow, title, sub, guide, ring }) => (
  <section className="bg-[#FDFBF7] pt-14 pb-16">
    <div className="mx-auto max-w-5xl px-5 md:px-8 flex flex-col md:flex-row items-center gap-8">
      <div className="flex-1 rise-in">
        <span className="font-hand text-2xl text-[#4A90E2]">{eyebrow}</span>
        <h1 className="font-head font-700 tracking-tight text-4xl sm:text-5xl text-[#1E293B] mt-1">{title}</h1>
        <p className="mt-4 font-body text-lg text-[#475569] leading-relaxed max-w-2xl">{sub}</p>
      </div>
      {guide && <GuideMascot src={guide} size={150} ring={ring} className="animate-floaty" />}
    </div>
  </section>
);

const Block = ({ title, children, tint = "#ffffff" }) => (
  <div className="rounded-3xl p-8 shadow-soft-sm border border-black/5" style={{ background: tint }}>
    <h3 className="font-head font-600 text-2xl text-[#1E293B]">{title}</h3>
    <div className="mt-3 font-body text-[#475569] leading-relaxed space-y-3">{children}</div>
  </div>
);

const Philosophy = () => {
  return (
    <div data-testid="philosophy-page">
      <PageHero
        eyebrow="What we believe"
        title="Our Educational Philosophy"
        sub="At Divinities Cove, we believe children are naturally curious, capable, and wise. Education should support the child — not require the child to adapt to the system."
        guide={GUIDES.book}
        ring="#B983FF"
      />

      <section className="py-16 bg-white">
        <div className="mx-auto max-w-6xl px-5 md:px-8 grid md:grid-cols-2 gap-6">
          <Block title="🌱 Child-Led & Unschooling-Inspired" tint="#eaf2fc">
            <p>Our program is built upon an unschooling-inspired, child-led philosophy combined with holistic care, Indigenous knowledge, and intentional guidance from caring adults.</p>
            <p>Every child learns differently, develops at a different pace, and carries unique gifts. Our responsibility is to observe, encourage, and create meaningful opportunities that allow those gifts to flourish.</p>
          </Block>
          <Block title="🌿 Indigenous-Based Learning" tint="#eef4ef">
            <p>Divinities Cove is proudly rooted in Indigenous values that teach respect for the Earth, community, ancestry, relationships, and reciprocity.</p>
            <p>Children develop a meaningful relationship with the natural world through nature exploration, gardening, seasonal rhythms, stories & teachings, gratitude practices, and learning through observation and experience.</p>
          </Block>
          <Block title="✨ Religious & Universal Studies" tint="#f4edff">
            <p>We introduce children to age-appropriate teachings from a variety of world traditions while honoring each family's beliefs.</p>
            <p>Children may explore nature-based wisdom, world cultures, stories from diverse faith traditions, and universal values like kindness, honesty, gratitude, and peace. Our goal is not to promote one religion, but to cultivate understanding, respect, and shared humanity.</p>
          </Block>
          <Block title="🎨 Life Becomes the Curriculum" tint="#fdf1e6">
            <p>Children may learn mathematics while cooking, literacy through storytelling, science during nature walks, history through cultural traditions, and leadership through community projects.</p>
            <p>We value process over perfection and discovery over memorization.</p>
          </Block>
        </div>
      </section>

      <section className="py-16 bg-[#FDFBF7]">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <SectionHeading center eyebrow="How we approach unschooling" title="Learning as a natural part of life" />
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {PHILOSOPHY_PRINCIPLES.map((p) => (
              <div key={p} className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-soft-sm" data-testid="principle-item">
                <CheckCircle2 className="text-[#84A98C] mt-0.5 shrink-0" size={22} />
                <span className="font-body text-[#1E293B]">{p}</span>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center"><PillLink to="/pillars" variant="primary" testid="philosophy-to-pillars">Explore Our Five Pillars</PillLink></div>
        </div>
      </section>
    </div>
  );
};

export default Philosophy;
